<template>
  <div class="vb-result">
    <not-found v-if="products.length === 0" :icon="'palm'" />

    <b-row v-else>
      <b-col cols="12" class="text-center">
        <h1 class="vb-heading vb-heading--h1">{{location.label}}</h1>
        <p v-if="!is_only_stay_package">Volo da
          <span
            class="text-capitalize"
            v-for="(airport, index) in airports_selected"
            :key="`${airport.iata_code}:${index}`"
          >
            {{airport.name.toLowerCase()}} ({{airport.iata_code}})
          </span> -
          {{occupancy[0].adults + occupancy[0].children.length}} ospiti -
          {{ formatted_departure }}
          ({{nights}} notti)
        </p>
        <p v-else-if="is_only_stay_package">
          {{occupancy[0].adults + occupancy[0].children.length}} ospiti -
          {{ formatted_checkin }}
          -
          {{ formatted_checkout }}
        </p>
      </b-col>

      <b-col cols="12">
        <results-controls
          v-if="!loading"
          :products="products"
          :filtered="filtered_products"
          :statics="statics"
          :sort="sort"
          :active_filters="{
            filter_keyd: filter_keyd,
            filter_accommodation: filter_accommodation,
            filter_rating: filter_rating,
            filter_airport: filter_airport,
            filter_brand: filter_brand,
            filter_availability: filter_availability,
          }"
          @order="on_order"
          @filter="on_filter"
          @reset="on_reset"
        />
      </b-col>

      <b-col cols="12">
        <dynamic-scroller
          v-if="!loading"
          page-mode
          ref='results-scroller'
          :items="filtered_products"
          :min-item-size="204"
          :emitUpdate="true"
          @update="on_scroll_update"
          key-field="pidkey"
        >
          <template v-slot="{ item, index, active }">
            <dynamic-scroller-item
              class="pb-4"
              :item="item"
              :data-index="index"
              :active="active"
              :size-dependencies="[]"
            >
              <pad
                v-if="item.type === 'product'"
                :product="item"
                :statics="statics[item.pid]"
                :occupancy="occupancy"
                :keyds="keyds"
                :brand="get_brand(item.pid.split('-')[1])"
              />
              <cta-credit v-if="item.type === 'credit'" />
            </dynamic-scroller-item>
          </template>
        </dynamic-scroller>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { parse_date, deep_clone, custom_event } from '@utilities/commons'
import { get_brand } from '@alpitour/resources'
import { apply_filters } from '@alpitour/utilities/results-filters'
import { apply_sort, inject_credit_pads } from '@alpitour/utilities/results-sorting'
import ResultsControls from './results-controls/results-controls.vue'
import CtaCredit from '@alpitour/components/blocks/cta-credit'
import NotFound from '@/components/blocks/not-found'
import { mapState, mapGetters } from 'vuex/dist/vuex.common.js'

export default {
  name: "results-block",
  components: {
    'results-controls': ResultsControls,
    'cta-credit': CtaCredit,
    'not-found': NotFound,
  },
  created() {
    this.parse_date = parse_date
    this.get_brand = get_brand
  },
  data() {
    return {
      location: this.$store.state.liveat.location,
      departure_airports: this.$store.state.liveat.departure_airports,
      occupancy: deep_clone(this.$store.state.occupancy),
      departure_date: this.$store.state.liveat.departure_date,
      nights: this.$store.state.liveat.nights,
      checkin_date: this.$store.state.liveat.checkin_date,
      checkout_date: this.$store.state.liveat.checkout_date,
      loading: true,
      filtered_products: [],
      scroller_interval: null,
    }
  },
  computed: {
    ...mapState('liveat', [
      'package_type', 'products', 'statics', 'keyds',
      'filter_keyd', 'filter_accommodation', 'filter_rating',
      'filter_airport', 'filter_brand', 'filter_availability',
      'sort', 'offset_scroll_search',
    ]),
    ...mapGetters('liveat', ['is_only_stay_package']),
    airports_selected() {
      return this.departure_airports.filter((a) => a.selected)
    },
    formatted_departure() {
      return this.format_date(this.departure_date);
    },
    formatted_checkin() {
      return this.format_date(this.checkin_date);
    },
    formatted_checkout() {
      return this.format_date(this.checkout_date);
    },
  },
  watch: {
    products() {
      this.run_filter_pipeline()
    },
  },
  mounted() {
    this.filtered_products = this.products
    this.run_filter_pipeline()
    this.loading = false
    if (this.products.length === 0) {
      // TODO: deprecated event name — rename to colon format (e.g. 'results:empty') to align
      // with the newer <feature>:<phase>:<action> convention.
      // Flagged via custom_event's `deprecated` arg → `deprecated: true` in the Rybbit payload.
      custom_event('no_results', '', 0, '', true)
    }
    if (this.offset_scroll_search === 0) { 
      return; 
    }
    
    this.$nextTick(() => {
      this.scroller_interval = setInterval(() => { 
        if (!this.$refs['results-scroller']) {
          this.clean_scroller_interval();
          return
        }
        const { scroller } = this.$refs['results-scroller'].$refs || {}
        if (!scroller) {
          this.clean_scroller_interval();
          return
        }
        if (scroller.ready) { 
          this.clean_scroller_interval(); 
          scroller.scrollToPosition(this.offset_scroll_search);
        }
      }, 100)
    })
  },
  beforeDestroy() {
    this.clean_scroller_interval();
  },
  methods: {
    format_date(date_string) {
      const d = parse_date(date_string);
      return `${d.day_number} ${d.month_string} ${d.year}`;
    },
    on_order(value) {
      this.$store.commit('liveat/set_store_values', { sort: value })
      this.run_filter_pipeline()
    },

    on_filter({ key, filters }) {
      this.$store.commit('liveat/set_store_values', { [`filter_${key}`]: filters })
      this.run_filter_pipeline()
    },

    on_reset() {
      this.$store.commit('liveat/set_store_values', {
        filter_keyd: [],
        filter_accommodation: [],
        filter_rating: [],
        filter_airport: [],
        filter_brand: [],
        filter_availability: false,
      })
      this.run_filter_pipeline()
    },

    run_filter_pipeline() {
      const availability_total = this.get_availability().total
      const active = {
        filter_keyd: this.filter_keyd,
        filter_accommodation: this.filter_accommodation,
        filter_rating: this.filter_rating,
        filter_airport: this.filter_airport,
        filter_brand: this.filter_brand,
        filter_availability: this.filter_availability,
      }

      const { result, empty_filter } = apply_filters({
        products: this.products,
        active_filters: active,
        availability_total,
        markup_fn: (price) => this.markup(price),
      })

      if (result === null) {
        this.$nextTick(() => {
          this.on_filter({ key: empty_filter, filters: [] })
        })
        return
      }

      const sorted = apply_sort(result, this.sort)
      this.filtered_products = inject_credit_pads(sorted)
    },

    on_scroll_update() {
      if (!this.$refs['results-scroller']) {
        return;
      }
      const { scroller } = this.$refs['results-scroller'].$refs || {}
      if (!scroller) { 
        return; 
      }
      const { start: scroll = 0 } = scroller.getScroll()
      if (scroll === 0) { 
        return; 
      }
      this.$store.commit('liveat/set_store_values', { offset_scroll_search: scroll })
    },

    clean_scroller_interval() {
      if (this.scroller_interval) {
        clearInterval(this.scroller_interval);
        this.scroller_interval = null;
      }
    }
  },
}
</script>

<style lang="scss">
.vb-result {
  .vb-heading--h1 {
    text-transform: lowercase;

    &:first-letter {
      text-transform: uppercase;
    }
  }
}
</style>
